/**
 * Turning an app refusal into a sentence the reader can act on.
 *
 * The server states the rule and names the app it is about — the problem's
 * `code` and its `module` member. It also sends a `detail`, but that string is
 * written once, in English, and this ERP is read in Arabic. So the code and
 * the app are what get translated here; the server's prose is the last resort
 * for a refusal this file has not been taught yet.
 */
import type { TFunction } from "i18next";

import { ApiError } from "@/lib/api/errors";
import type { AppCode } from "@/lib/modules";

/** The app a refusal is about, when the server named one. */
function subject(error: ApiError): AppCode | null {
  const problem = error.problem as (Record<string, unknown> & { module?: unknown }) | undefined;
  const named = problem?.module;
  return typeof named === "string" && named !== "" ? (named as AppCode) : null;
}

export function refusalMessage(t: TFunction, error: unknown): string {
  if (!(error instanceof ApiError)) return "";
  const code = error.code ?? "";
  const app = subject(error);
  const name = app === null ? "" : t(`apps.${app}.name`, app);

  switch (code) {
    case "company_module.dependency_not_installed":
      return t("apps.installFirst", "Install {{name}} first.", { name });
    case "company_module.dependent_module_installed":
      return t("apps.removeFirst", "Remove {{name}} first.", { name });
    case "company_module.not_removable":
      return t("apps.cannotRemove", "This app cannot be removed.");
    case "company_module.precondition_failed":
      // Somebody else changed this app while the screen was open. Reloading is
      // the honest instruction, because the version this screen holds is stale.
      return t("apps.installFailed", "That app could not be installed.");
    default:
      // An unrecognised refusal still has to say something true, so fall back
      // to whatever the server wrote rather than inventing a reason.
      return error.problem?.detail ?? code;
  }
}
